import { DiscountUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'kz_discount_link';
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;
const SOURCE_ID = 'kz-discount-link';

/**
 * Keeps campaign links on the landing page while establishing Shopify's
 * native discount session in the background.
 */
export function normalizeDiscountCode(value) {
  const code = value?.trim();
  return code && /^[a-z0-9_-]{1,100}$/i.test(code) ? code : null;
}

export function readStoredDiscount(storage, now = Date.now()) {
  try {
    const stored = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
    if (!stored || stored.expiresAt <= now) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalizeDiscountCode(stored.code);
  } catch (_error) {
    storage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function createDiscountUrl(code, root, origin) {
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const url = new URL(`${normalizedRoot}discount/${encodeURIComponent(code)}`, origin);
  url.searchParams.set('redirect', `${normalizedRoot}cart.js`);
  return url;
}

function storeDiscount(storage, code) {
  storage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      code,
      expiresAt: Date.now() + STORAGE_TTL_MS,
    })
  );
}

function cartContainsCode(cart, code) {
  return cart.discount_codes?.some((discount) => discount.code.toLowerCase() === code.toLowerCase()) ?? false;
}

function isProtectedSubmit(event) {
  if (!(event.target instanceof HTMLFormElement)) return false;

  const action = new URL(event.target.action || window.location.href, window.location.origin).pathname;
  const isCartAdd = action.endsWith('/cart/add');
  const isCheckout = event.submitter instanceof HTMLElement && event.submitter.getAttribute('name') === 'checkout';
  return isCartAdd || isCheckout;
}

function lockAcceleratedCheckout() {
  const selector = '.accelerated-checkout-block, .additional-checkout-buttons, shopify-accelerated-checkout';

  for (const element of document.querySelectorAll(selector)) {
    if (!(element instanceof HTMLElement) || element.inert) continue;
    element.inert = true;
    element.dataset.kzDiscountPending = 'true';
    element.setAttribute('aria-busy', 'true');
  }
}

function unlockAcceleratedCheckout() {
  for (const element of document.querySelectorAll('[data-kz-discount-pending="true"]')) {
    if (!(element instanceof HTMLElement)) continue;
    element.inert = false;
    delete element.dataset.kzDiscountPending;
    element.removeAttribute('aria-busy');
  }
}

export function initializeDiscountLink({
  location = window.location,
  storage = window.localStorage,
  fetchImpl = window.fetch.bind(window),
  root = window.Shopify?.routes?.root || '/',
} = {}) {
  const urlCode = normalizeDiscountCode(new URLSearchParams(location.search).get('discount'));
  const code = urlCode || readStoredDiscount(storage);
  if (!code) return null;

  if (urlCode) storeDiscount(storage, code);

  let pending = true;
  let replayingSubmit = false;
  const discountUrl = createDiscountUrl(code, root, location.origin);

  const ready = fetchImpl(discountUrl, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Discount session failed with HTTP ${response.status}`);
      return response.json();
    })
    .then((cart) => {
      if (!cartContainsCode(cart, code)) {
        throw new Error(`Shopify did not retain discount code ${code}`);
      }
      document.dispatchEvent(new DiscountUpdateEvent(cart, SOURCE_ID));
      return cart;
    })
    .catch((error) => {
      console.warn('[KryoZon] Unable to prepare campaign discount.', error);
      return null;
    })
    .finally(() => {
      pending = false;
      observer.disconnect();
      unlockAcceleratedCheckout();
    });

  const observer = new MutationObserver(lockAcceleratedCheckout);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  lockAcceleratedCheckout();

  document.addEventListener(
    'submit',
    (event) => {
      if (!pending || replayingSubmit || !isProtectedSubmit(event)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const form = event.target;
      const submitter = event.submitter;

      ready.finally(() => {
        replayingSubmit = true;
        form.requestSubmit(submitter instanceof HTMLElement ? submitter : undefined);
        queueMicrotask(() => {
          replayingSubmit = false;
        });
      });
    },
    true
  );

  document.addEventListener('discount:update', (event) => {
    if (event.detail?.sourceId === SOURCE_ID) return;
    const cart = event.detail?.resource;
    if (cart?.discount_codes && !cartContainsCode(cart, code)) storage.removeItem(STORAGE_KEY);
  });

  window.KryoZonDiscountLink = { code, ready };
  return window.KryoZonDiscountLink;
}

initializeDiscountLink();
